/**
 * Model that can be used to synchronize the fetching of data for other models. This is useful when you want to
 * do something only after several models have been fetched.
 */
import Backbone from 'backbone';

import TrackedModel from 'utils/TrackedModel';

var Events = {
    SYNCHRONIZED: 'SYNCHRONIZED',
    SYNCHRONIZE_FAILED: 'SYNCHRONIZE_FAILED',
    ON_DOCUMENT_READY: 'ON_DOCUMENT_READY'
};

var modelSynchronizer = Backbone.Model.extend({
    initialize: function() {
        this.models = new Backbone.Collection();
        this.eventModels = [];
        this.requiredModelCount = 0;
        this.successfulFetches = 0;
        this.isSynchronized = false;
    },

    /**
     * Adds a model to be managed.
     * @param options options that can be used.  See TrackedModel's defaults for the complete set of options
     */
    addModel: function(options) {
        if (!options.model) {
            throw 'Model is required.';
        }

        var trackedModel = new TrackedModel(options);
        this.models.add(trackedModel);

        if (options.required) {
            this.requiredModelCount++;
        }
    },

    /**
     * This model can also wait for its tracked models to signal an event. This is useful for when a model needs to do
     * some post processing before you can consider its fetch completed.
     *
     * @param options the options for adding the event:
     *          model: The model in which to listen for the event.
     *          event: The event to listen for.
     */
    addEvent: function(options) {
        if (!options.model) {
            throw 'Model is required.';
        }

        if (!options.event) {
            throw 'Event is required.';
        }

        this.eventModels.push(options.model);
        this.requiredModelCount++;
        this.listenToOnce(options.model, options.event, this.onTrackedEvent);
    },

    /**
     *  Initiates the fetching of the models.
     */
    fetchModels: function() {
        // If no models, signal immediately. Not common but can happen in frameworks.
        if (this.models.length == 0) {
            this.fireIfComplete();
            return;
        }

        var _this = this;
        jQuery(function() {
            _this.trigger(Events.ON_DOCUMENT_READY);
        });

        this.models.each(function(trackedModel) {
            var options = _.clone(trackedModel.options);
            options.success = function(model, response) {
                return _this.successFunction(trackedModel, response);
            };
            options.error = function(model, response) {
                return _this.errorFunction(trackedModel, response);
            };

            trackedModel.get('model').fetch(options);
        }, this);
    },

    /**
     * Called for each model when it is fetched successfully.
     *
     * @param trackedModel The TrackedModel the fetch completed for.
     * @param response The response for message for the fetch.
     */
    successFunction: function(trackedModel, response) {
        if (trackedModel.get('required')) {
            this.successfulFetches++;
            this.fireIfComplete();
        }

        var success = trackedModel.get('success');
        if (success) {
            success.call(this, trackedModel, response);
        }
    },

    /**
     * Called for each model that has a fetched failure.
     *
     * @param trackedModel The TrackedModel the fetch errored for.
     * @param response The response for message for the fetch.
     */
    errorFunction: function(trackedModel, response) {
        if (trackedModel.get('required')) {
            // Sometimes a failure is a valid use case
            if (trackedModel.get('countError')) {
                this.successfulFetches++;
                this.fireIfComplete();
            } else {
                if (trackedModel.get('required')) {
                    this.trigger(Events.SYNCHRONIZE_FAILED, response);
                }
            }
        }

        var error = trackedModel.get('error');
        if (error) {
            error.call(this, trackedModel, response);
        }
    },

    /**
     * Called when a tracked event has been fired.
     */
    onTrackedEvent: function() {
        this.successfulFetches++;
        this.fireIfComplete();
    },

    /**
     * Fires the complete event if the conditions are satisfied.
     */
    fireIfComplete: function() {
        if (this.successfulFetches == this.requiredModelCount) {
            // Unbind all events that have been bound. Help with memory recollection and
            // prevent additional events from firing
            //                _.each(this.eventModels, function(model) {
            //                    model.unbind(null, null, this);
            //                }, this);
            this.isSynchronized = true;
            this.trigger(Events.SYNCHRONIZED);
        } else if (this.successfulFetches > this.requiredModelCount) {
            throw 'Something is wrong.';
        }
    },

    synchronized: function() {
        return this.isSynchronized;
    }
});

modelSynchronizer.Events = Events;

export default modelSynchronizer;
